import React from 'react';
import { useTranslation } from 'react-i18next';
import type { MfaMethod } from '../../types';
import { BRAND, BRAND_LIGHT } from '../../lib/theme';

export interface MethodChoiceStepProps {
  selected: MfaMethod;
  onSelect: (method: MfaMethod) => void;
  onContinue: () => void;
  onCancel?: () => void;
  /** In force-setup mode, default focus goes to 'email' (immediately usable). */
  forceSetup?: boolean;
}

interface MethodOption {
  method: MfaMethod;
  icon: React.ReactNode;
  recommended?: boolean;
}

const EMAIL_ICON = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const SMS_ICON = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const TOTP_ICON = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
    <path d="M12 18h.01"/>
    <path d="M9 7h6"/>
    <path d="M9 11h6"/>
  </svg>
);

const METHODS: MethodOption[] = [
  { method: 'email',  icon: EMAIL_ICON },
  { method: 'sms',    icon: SMS_ICON },
  { method: 'totp',   icon: TOTP_ICON, recommended: true },
];

export function MethodChoiceStep({
  selected,
  onSelect,
  onContinue,
  onCancel,
}: MethodChoiceStepProps) {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#101828', marginBottom: 6 }}>
          {t('mfa.setup.step_method.title')}
        </h2>
        <p style={{ fontSize: 14, color: '#667085', lineHeight: 1.6 }}>
          {t('mfa.setup.step_method.subtitle')}
        </p>
      </div>

      <div
        role="radiogroup"
        aria-label={t('mfa.setup.step_method.title')}
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {METHODS.map(({ method, icon, recommended }) => {
          const isSelected = selected === method;
          return (
            <label
              key={method}
              htmlFor={`method-${method}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: 16,
                borderRadius: 10,
                border: `2px solid ${isSelected ? BRAND : '#e4e7ec'}`,
                background: isSelected ? BRAND_LIGHT : '#fff',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <input
                id={`method-${method}`}
                type="radio"
                name="mfa-setup-method"
                value={method}
                checked={isSelected}
                onChange={() => onSelect(method)}
                aria-label={`${t(`mfa.setup.method.${method}.title`)}${recommended ? ` (${t('mfa.setup.method.recommended_badge')})` : ''}`}
                style={{ marginTop: 2, accentColor: BRAND, cursor: 'pointer' }}
              />
              <span style={{ color: isSelected ? BRAND : '#667085', flexShrink: 0, marginTop: 1 }}>
                {icon}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#101828' }}>
                    {t(`mfa.setup.method.${method}.title`)}
                  </span>
                  {recommended && (
                    <span style={{
                      fontSize: 10,
                      fontWeight: 600,
                      background: '#ecfdf3',
                      color: '#027a48',
                      borderRadius: 9999,
                      padding: '2px 8px',
                    }}>
                      {t('mfa.setup.method.recommended_badge')}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 12, color: '#667085', margin: 0, lineHeight: 1.5 }}>
                  {t(`mfa.setup.method.${method}.description`)}
                </p>
              </div>
            </label>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        {onCancel && (
          <button type="button" onClick={onCancel} style={secondaryBtn}>
            {t('common.action.cancel')}
          </button>
        )}
        <button type="button" onClick={onContinue} style={primaryBtn}>
          {t('common.action.continue')} →
        </button>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  background: BRAND,
  color: '#fff',
  border: 'none',
  borderRadius: 9,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'Outfit, sans-serif',
};

const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: '#344054',
  border: '1px solid #e4e7ec',
  borderRadius: 9,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'Outfit, sans-serif',
};
